import { useState } from 'react'
import { DataGrid, GridPaginationModel, GridColDef, DataGridProps } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { PrometheusAlert } from '../types'
import {
  translateAlertPhase,
  getAlertTimestamp,
  getHumanizedAlertValue,
  getUniqueAlerts,
  translateAlertRuleName,
  translateAlertSeverity,
  getAlertValue,
  getAlertThresholdValue,
  getHumanizedAlertThresholdValue
} from '../utils/alert'
import { humanizeTimestamp } from '../utils/date'
import AlertRuleLabel from './AlertRuleLabel'
import AlertSeverityChip from './AlertSeverityChip'

interface AlertDataGridProps extends Omit<DataGridProps, 'rows' | 'columns'> {
  alerts: PrometheusAlert[]

  /**
   * Indicates if only unique alerts are displayed.
   */
  showUnique?: boolean
}

export default function AlertDataGrid({ alerts, showUnique = true }: AlertDataGridProps) {
  /**
   * The translation function.
   */
  const [t] = useTranslation()

  /**
   * The pagination model.
   */
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 24,
    page: 0
  })

  /**
   * The columns.
   */
  const columns: GridColDef<PrometheusAlert>[] = [
    {
      field: 'activeAt',
      headerName: t('time'),
      type: 'date',
      width: 200,
      editable: false,
      valueGetter: (_, row) => getAlertTimestamp(row),
      valueFormatter: (value: Date | null) => {
        if (value) {
          return humanizeTimestamp(value)
        } else {
          return null
        }
      }
    },
    {
      field: 'labels.alertname',
      headerName: t('name'),
      type: 'string',
      width: 200,
      editable: false,
      valueGetter: (_, row) => translateAlertRuleName(row.labels.alertname),
      renderCell: ({ row }) => <AlertRuleLabel alertRule={row.labels.alertname} />
    },
    {
      field: 'labels.name',
      headerName: t('phase'),
      type: 'string',
      width: 200,
      editable: false,
      valueGetter: (_, row) => translateAlertPhase(row.labels.name)
    },
    {
      field: 'labels.severity',
      headerName: t('severity'),
      type: 'string',
      width: 200,
      editable: false,
      valueGetter: (_, row) => translateAlertSeverity(row.labels.severity),
      renderCell: ({ row }) => <AlertSeverityChip severity={row.labels.severity} size="small" />
    },
    {
      field: 'value',
      headerName: t('value'),
      type: 'number',
      align: 'right',
      flex: 1,
      width: 100,
      editable: false,
      valueGetter: (_, row) => getAlertValue(row),
      valueFormatter: (_, row) => getHumanizedAlertValue(row)
    },
    {
      field: 'labels.param1',
      headerName: t('threshold'),
      type: 'number',
      align: 'right',
      width: 100,
      editable: false,
      valueGetter: (_, row) => getAlertThresholdValue(row),
      valueFormatter: (_, row) => getHumanizedAlertThresholdValue(row)
    }
  ]

  /**
   * The unique alerts by alert name and phase.
   */
  const uniqueAlerts = getUniqueAlerts(alerts)

  /**
   * The shown alerts.
   */
  const shownAlerts = showUnique ? uniqueAlerts : alerts

  /**
   * Get the row ID.
   */
  const handleRowId = (alert: PrometheusAlert) => {
    let rowId = `${alert.labels?.device ?? alert.labels?.ruuvitag ?? 'device'}-${
      alert.labels.alertname
    }-${alert.labels.name}`

    if (alert?.startsAt) {
      rowId = `${rowId}-${new Date(alert.startsAt).getTime()}`
    } else if (alert?.activeAt) {
      rowId += `${rowId}-${new Date(alert.activeAt).getTime()}`
    }

    return rowId
  }

  return (
    <DataGrid
      rows={shownAlerts}
      columns={columns}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      disableRowSelectionOnClick
      getRowId={handleRowId}
      sx={{ border: 0 }}
    />
  )
}
